import React, { ReactElement } from 'react';
import LoadingIndicator from '@Common/LoadingIndicator/LoadingIndicator';
import Notification from '@Common/Notification';
import { useLoadLocationLinks, useDeactivateProjectResource, DashboardLink } from '@Apis/locationDashboard';
import { useUpdateLocationTaskLink } from '@Apis/locationTask';
import { LocationDetailData } from '../../types';
import './LocationDetailLinksTile.scss';

interface LocationDetailLinksTileProps {
    location: LocationDetailData;
    projectIds: number[];
}

const linkKey = (link: DashboardLink) => `${link.source}-${link.id}`;

const LocationDetailLinksTile = ({ location, projectIds }: LocationDetailLinksTileProps): ReactElement => {
    const { data: linksData, isPending: isLoading } = useLoadLocationLinks(
        location.locationKey,
        projectIds
    );
    const links = linksData?.links ?? [];

    const updateLink = useUpdateLocationTaskLink(location.locationKey, projectIds);
    const deactivateResource = useDeactivateProjectResource(location.locationKey, projectIds);

    const openLink = (link: DashboardLink) => {
        if (/^\s*javascript:/i.test(link.url)) {
            Notification('error', 'Error', 'Invalid link URL.');
            return;
        }
        window.open(link.url, '_blank', 'noopener,noreferrer');
    };

    const onDelete = async (e: React.MouseEvent, link: DashboardLink) => {
        e.stopPropagation();
        try {
            if (link.source === 'task') {
                await updateLink.mutateAsync({
                    id: link.id,
                    locationTaskId: link.locationTaskId,
                    name: link.name,
                    url: link.url,
                    ordinal: link.ordinal,
                    isActive: false,
                    baselineTaskLinkId: null,
                    customerFileId: null,
                    editingName: '',
                    editingUrl: '',
                });
            } else {
                await deactivateResource.mutateAsync(link.projectResourceId!);
            }
            Notification('success', 'Link Deleted', '');
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };

    return (
        <div className='location-detail-links-tile'>
            <LoadingIndicator isLoading={isLoading}>
                <div className='link-cards'>
                    {links.map(link => (
                        <div
                            key={linkKey(link)}
                            className='link-card'
                            role='button'
                            tabIndex={0}
                            onClick={() => openLink(link)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') openLink(link);
                                if (e.key === ' ') { e.preventDefault(); openLink(link); }
                            }}
                        >
                            <div className='link-card-body'>
                                <div className='link-name'>{link.name}</div>
                                <div className='link-url'>
                                    <i className='fa-regular fa-arrow-up-right-from-square' />
                                    <span>{link.url}</span>
                                </div>
                            </div>
                            <button
                                type='button'
                                className='link-card-delete'
                                title='Delete link'
                                onClick={e => onDelete(e, link)}
                                aria-label='Delete link'
                            >
                                <i className='fas fa-trash-can' />
                            </button>
                        </div>
                    ))}

                    {!isLoading && links.length === 0 && (
                        <div className='links-empty'>No links</div>
                    )}
                </div>

                <div className='links-footer'>
                    <span className='links-count'>
                        {links.length} link{links.length === 1 ? '' : 's'}
                    </span>
                    <button type='button' className='add-link-btn'>
                        <i className='fa-regular fa-plus' />
                        <span>Add Link</span>
                    </button>
                </div>
            </LoadingIndicator>
        </div>
    );
};

export default LocationDetailLinksTile;
